import React, { useState, useEffect, useMemo } from 'react'
import { useGlobalState } from '@/hooks/context/global/useGlobalState'
import { useVehicleFilter } from '@/hooks/context/global/useVehicleFilter'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Search, X, SlidersHorizontal, Bike, Car, Sparkles, Layers } from 'lucide-react'
import { Brand, Vehicle, VehicleCategory, VehicleCategoryType, VehicleCondition } from '@/types'
import { VehicleCard } from '@/components/VehicleCard'
import { motion } from 'framer-motion'
import { EmptyResult } from '@/components/home/EmptyResult'
import { Badge } from '@/components/ui/badge'
import { 
    Pagination, 
    PaginationContent, 
    PaginationItem, 
    PaginationLink, 
} from "@/components/ui/pagination"
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from "@/components/ui/select"
import { LayoutGrid, List, Grid3x3 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { NumericFormat } from 'react-number-format'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { AppCatalogFooter } from '@/components/layout/AppCatalogFooter'
import { useRepuestosLayout } from '@/hooks/context/global/useRepuestosLayout'
import { useCatalogPrefs } from '@/hooks/context/global/useCatalogPrefs'
import { formatPrice, normalizeCurrency } from '@/lib/currency'

export default function Catalog() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { vehicles, brands, categories, setSelectedVehicle, myInit, setMyInit } = useGlobalState()
    const { sidebarCollapsed, searchOpen, setSearchOpen } = useRepuestosLayout()
    const { currency, viewMode, setViewMode, showInitialPrice, setShowInitialPrice } = useCatalogPrefs()
    const {
        filteredVehicles,
        selectedBrands,
        selectedCategories,
        setSelectedBrands,
        setSelectedCategories,
        searchText,
        setSearchText,
        updateFilters,
        loading
    } = useVehicleFilter()

    const [itemsPerPage, setItemsPerPage] = React.useState(8)
    const [currentPage, setCurrentPage] = React.useState(1)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [selectedVehicleType, setSelectedVehicleType] = useState<'all' | VehicleCategoryType>('all')
    const [selectedCondition, setSelectedCondition] = useState<'all' | VehicleCondition>('all')
    /** El prompt inteligente de tipo ya fue respondido/descartado por el usuario. */
    const [typePromptDismissed, setTypePromptDismissed] = useState(false)
    const [sortMode, setSortMode] = useState<'featured' | 'price-asc' | 'price-desc' | 'newest'>('featured')

    const SIDEBAR_BRAND_LOGOS: Record<string, string> = {
        // Automóviles (copiados desde mobile)
        audi: '/brands/automobiles/audi.png',
        bmw: '/brands/automobiles/bmw.png',
        changan: '/brands/automobiles/changan.png',
        chevrolet: '/brands/automobiles/chevrolet.png',
        daihatsu: '/brands/automobiles/daihatsu.png',
        ford: '/brands/automobiles/ford.png',
        hondaauto: '/brands/automobiles/hondaauto.png',
        hondacar: '/brands/automobiles/hondaauto.png',
        hyundai: '/brands/automobiles/hyundai.png',
        kia: '/brands/automobiles/kia.png',
        mazda: '/brands/automobiles/mazda.png',
        mercedes: '/brands/automobiles/mercedes.png',
        mercedesbenz: '/brands/automobiles/mercedes.png',
        nissan: '/brands/automobiles/nissan.png',
        toyota: '/brands/automobiles/toyota.png',
        volkswagen: '/brands/automobiles/volkswagen.png',

        // Motocicletas (ya existentes en frontend/public/brands)
        bajaj: '/brands/bajaj.png',
        hero: '/brands/hero.png',
        honda: '/brands/honda.png',
        ktm: '/brands/ktm.png',
        loncin: '/brands/loncin.png',
        nipponia: '/brands/nipponia.png',
        pgo: '/brands/pgo.png',
        supergato: '/brands/supergato.png',
        supertucan: '/brands/supertucan.png',
        suzuki: '/brands/suzuki.png',
        tvs: '/brands/tvs.png',
        yamaha: '/brands/yamaha.png',
        voge: '/brands/voge.png',
        ducati: '/brands/ducati.png',
    }

    // Actualizar filtros cuando cambie la selección o cuando lleguen los vehículos
    useEffect(() => {
        updateFilters()
    }, [selectedBrands, selectedCategories, searchText, vehicles, updateFilters])

    // Reset page when filters change
    React.useEffect(() => {
        setCurrentPage(1)
    }, [filteredVehicles.length])

    const getVehicleType = (vehicle: Vehicle): VehicleCategoryType => {
        if (vehicle.type === VehicleCategoryType.AUTOMOBILE) return VehicleCategoryType.AUTOMOBILE
        return VehicleCategoryType.MOTORCYCLE
    }

    const normalizeBrandKey = (value?: string) => {
        if (!value) return ''
        return value
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '')
    }

    const getSidebarBrandLogo = (brand: Brand) => {
        const key = normalizeBrandKey(brand.name)
        return SIDEBAR_BRAND_LOGOS[key] || brand.logo || null
    }

    useEffect(() => {
        const q = searchParams.get('q')
        const type = searchParams.get('type') as VehicleCategoryType | null
        const condition = searchParams.get('condition') as VehicleCondition | null
        const brandId = searchParams.get('brand')
        const budget = searchParams.get('budget')
        const sidebar = searchParams.get('sidebar')

        if (q) {
            setSearchText(q)
            setSearchOpen(true)
        }
        // Nueva búsqueda: el prompt inteligente vuelve a estar disponible.
        setTypePromptDismissed(false)
        if (budget) {
            const parsed = Number(budget)
            if (!Number.isNaN(parsed)) setMyInit(parsed)
        }

        if (type === VehicleCategoryType.AUTOMOBILE || type === VehicleCategoryType.MOTORCYCLE) {
            setSelectedVehicleType(type)
        }

        if (condition && Object.values(VehicleCondition).includes(condition)) {
            setSelectedCondition(condition)
        }

        if (brandId && brands.length > 0) {
            const brand = brands.find((b) => b.id === brandId)
            if (brand) {
                setSelectedBrands([brand])
            }
        }

        if (sidebar === '1') {
            setSidebarOpen(true)
        }
    }, [searchParams, brands, setSearchText, setMyInit, setSelectedBrands, setSearchOpen])

    const catalogVehicles = useMemo(() => {
        let list = filteredVehicles

        if (selectedVehicleType !== 'all') {
            list = list.filter((vehicle) => getVehicleType(vehicle) === selectedVehicleType)
        }

        if (selectedCondition !== 'all') {
            list = list.filter((vehicle) => vehicle.condition === selectedCondition)
        }

        switch (sortMode) {
            case 'price-asc':
                return [...list].sort((a, b) => (a.salePrice || 0) - (b.salePrice || 0))
            case 'price-desc':
                return [...list].sort((a, b) => (b.salePrice || 0) - (a.salePrice || 0))
            case 'newest':
                return [...list].sort((a, b) => (b.year || 0) - (a.year || 0))
            default:
                return list
        }
    }, [filteredVehicles, selectedVehicleType, selectedCondition, sortMode])

    // Tipos de vehículo presentes en los resultados de la búsqueda (antes del filtro de tipo).
    const typesInResults = useMemo(() => {
        let hasMoto = false
        let hasAuto = false
        for (const v of filteredVehicles) {
            if (getVehicleType(v) === VehicleCategoryType.AUTOMOBILE) hasAuto = true
            else hasMoto = true
            if (hasMoto && hasAuto) break
        }
        return { hasMoto, hasAuto }
    }, [filteredVehicles])

    // Prompt inteligente: solo si la búsqueda del hero trae tipos mixtos.
    const showTypePrompt =
        !!searchParams.get('q') &&
        !searchParams.get('type') &&
        !typePromptDismissed &&
        selectedVehicleType === 'all' &&
        typesInResults.hasMoto &&
        typesInResults.hasAuto

    const chooseVehicleType = (type: 'all' | VehicleCategoryType) => {
        setSelectedVehicleType(type)
        setTypePromptDismissed(true)
    }

    const automobileBrands = useMemo(
        () => brands.filter((brand) => brand.vehicleType === VehicleCategoryType.AUTOMOBILE),
        [brands]
    )
    const motorcycleBrands = useMemo(
        () => brands.filter((brand) => brand.vehicleType === VehicleCategoryType.MOTORCYCLE),
        [brands]
    )
    const uncategorizedBrands = useMemo(
        () => brands.filter((brand) => !brand.vehicleType),
        [brands]
    )
    const filteredCategoriesForSidebar = useMemo(
        () =>
            selectedVehicleType === 'all'
                ? categories
                : categories.filter(
                    (category) =>
                        !category.vehicleType || category.vehicleType === selectedVehicleType
                ),
        [categories, selectedVehicleType]
    )
    const brandGroupsForSidebar = useMemo(() => {
        if (selectedVehicleType === VehicleCategoryType.MOTORCYCLE) {
            return [{ title: 'Marcas de Motocicletas', items: motorcycleBrands }]
        }
        if (selectedVehicleType === VehicleCategoryType.AUTOMOBILE) {
            return [{ title: 'Marcas de Automóviles', items: automobileBrands }]
        }
        return [
            { title: 'Marcas de Motocicletas', items: motorcycleBrands },
            { title: 'Marcas de Automóviles', items: automobileBrands },
            { title: 'Marcas generales', items: uncategorizedBrands },
        ]
    }, [selectedVehicleType, motorcycleBrands, automobileBrands, uncategorizedBrands])

    const pageCount = Math.ceil(catalogVehicles.length / itemsPerPage)
    const currentItems = catalogVehicles.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    )

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value)
    }

    const handleVehicleClick = (vehicle: Vehicle) => {
        setSelectedVehicle(vehicle)
        navigate(`/vehiculo/${vehicle.id}`)
    }
    
    const handleCategoryClick = (category: VehicleCategory) => {
        const newSelectedCategories = selectedCategories.some(c => c.id === category.id)
            ? selectedCategories.filter(c => c.id !== category.id)
            : [...selectedCategories, category]
        setSelectedCategories(newSelectedCategories)
        updateFilters()
    }

    const handleBrandClick = (brand: Brand) => {
        const newSelectedBrands = selectedBrands.some(b => b.id === brand.id)
            ? selectedBrands.filter(b => b.id !== brand.id)
            : [...selectedBrands, brand]
        setSelectedBrands(newSelectedBrands)
        updateFilters()
    }

    const clearFilters = () => {
        setSelectedBrands([])
        setSelectedCategories([])
        setSearchText('')
        setSelectedVehicleType('all')
        setSelectedCondition('all')
    }

    const activeFiltersCount = selectedBrands.length + selectedCategories.length +
        (searchText ? 1 : 0) + (selectedVehicleType !== 'all' ? 1 : 0) +
        (selectedCondition !== 'all' ? 1 : 0)

    const handleChangePage = (page: number) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleChangeItemsPerPage = (value: string) => {
        setItemsPerPage(Number(value))
        setCurrentPage(1)
    }

    const getDisplayPrice = (vehicle: Vehicle) => {
        if (!vehicle) return 0
        if (showInitialPrice) {
            return vehicle.initPrice || Math.round((vehicle.salePrice ?? 0) * 0.3)
        }
        return vehicle.salePrice ?? 0
    }

    const getSecondaryPrice = (vehicle: Vehicle) => {
        if (!vehicle) return 0
        if (showInitialPrice) {
            return vehicle.salePrice ?? 0
        }
        return vehicle.initPrice || Math.round((vehicle.salePrice ?? 0) * 0.3)
    }

    const VehicleListItem = ({ vehicle, onClick }: { vehicle: Vehicle, onClick: () => void }) => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col gap-3 p-3 bg-white rounded-2xl border border-gray-200/80 shadow-[0_10px_24px_rgba(15,23,42,0.08)] hover:shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all cursor-pointer sm:flex-row sm:items-stretch sm:gap-4 sm:p-4"
            onClick={onClick}
        >
            <div className="w-full h-40 relative rounded-xl overflow-hidden shrink-0 sm:w-32 sm:h-24">
                {vehicle.images && vehicle.images.length > 0 ? (
                    <img
                        src={vehicle.images[0]}
                        alt={vehicle.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">Sin imagen</span>
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{vehicle.name}</h3>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-500">
                    {vehicle.brand?.logo && (
                        <img
                            src={vehicle.brand.logo}
                            alt={vehicle.brand.name}
                            className="w-4 h-4 object-contain"
                        />
                    )}
                    <span>{vehicle.brand?.name || 'Sin marca'}</span>
                    <span className="hidden sm:inline">•</span>
                    <span>{vehicle.category?.name || 'Sin categoría'}</span>
                </div>
                {vehicle.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {vehicle.description.slice(0, 100)}...
                    </p>
                )}
            </div>
            <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-between sm:text-right">
                <div>
                    <p className="text-xl sm:text-2xl font-bold text-brand-red">
                        {formatPrice(getDisplayPrice(vehicle), normalizeCurrency(vehicle.currency), currency)}
                    </p>
                    {showInitialPrice && (
                        <p className="text-xs sm:text-sm text-gray-500">
                            Inicial / Depósito mínimo
                        </p>
                    )}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation()
                        onClick()
                    }}
                >
                    Ver detalles
                </Button>
            </div>
        </motion.div>
    )

    const handleInitialChange = (values: any) => {
        const value = values.floatValue || 0
        setMyInit(value)
        updateFilters()
    }

    return (
        <div className="relative flex flex-col min-h-[calc(100vh-5rem)] bg-[#F2F2F6]">
            {/* FAB Filtros — móvil */}
            <Button
                variant="default"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden fixed bottom-20 left-4 z-40 h-12 rounded-full bg-[#1F2240] text-yellow-200 shadow-xl hover:bg-[#2A285D] px-4"
            >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                <span className="text-sm font-semibold">Filtros</span>
                {activeFiltersCount > 0 && (
                    <Badge className="ml-2 bg-[#E10F18] text-white px-1.5 min-w-[20px] h-5 flex items-center justify-center">
                        {activeFiltersCount}
                    </Badge>
                )}
            </Button>

            {/* Sidebar - Mobile Drawer */}
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetContent side="left" className="w-[320px] bg-white p-0">
                    <div className="p-4 space-y-5">
                        <SheetHeader className="px-1">
                            <SheetTitle>Filtros progresivos</SheetTitle>
                        </SheetHeader>

                        <ScrollArea className="h-[calc(100vh-140px)] pr-2">
                            <div className="space-y-5 pb-6">
                                <div className="rounded-xl border bg-gray-50 p-3 space-y-2">
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Buscar</Label>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            type="text"
                                            placeholder="Buscar vehículo o marca..."
                                            value={searchText}
                                            onChange={handleSearch}
                                            className="pl-9 h-9 bg-white"
                                        />
                                    </div>
                                </div>

                                <div className="rounded-xl border bg-gray-50 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Paso 1 · Tipo</p>
                                    <div className="mt-2 grid grid-cols-3 gap-2">
                                        <Button size="sm" variant={selectedVehicleType === 'all' ? 'default' : 'outline'} onClick={() => setSelectedVehicleType('all')}>Todos</Button>
                                        <Button size="sm" variant={selectedVehicleType === VehicleCategoryType.MOTORCYCLE ? 'default' : 'outline'} onClick={() => setSelectedVehicleType(VehicleCategoryType.MOTORCYCLE)}>Motos</Button>
                                        <Button size="sm" variant={selectedVehicleType === VehicleCategoryType.AUTOMOBILE ? 'default' : 'outline'} onClick={() => setSelectedVehicleType(VehicleCategoryType.AUTOMOBILE)}>Carros</Button>
                                    </div>
                                </div>

                                <div className="rounded-xl border bg-gray-50 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Condición</p>
                                    <div className="mt-2 grid grid-cols-3 gap-2">
                                        <Button size="sm" variant={selectedCondition === 'all' ? 'default' : 'outline'} onClick={() => setSelectedCondition('all')}>Todas</Button>
                                        <Button size="sm" variant={selectedCondition === VehicleCondition.NEW ? 'default' : 'outline'} onClick={() => setSelectedCondition(VehicleCondition.NEW)}>Nuevos</Button>
                                        <Button size="sm" variant={selectedCondition === VehicleCondition.USED ? 'default' : 'outline'} onClick={() => setSelectedCondition(VehicleCondition.USED)}>Usados</Button>
                                    </div>
                                </div>

                                <div className="rounded-xl border bg-gray-50 p-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Paso 2 · Marca</p>
                                        {selectedBrands.length > 0 && (
                                            <Button variant="ghost" size="sm" onClick={() => setSelectedBrands([])}>
                                                <X className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                    <div className="mt-2 space-y-3">
                                        {brandGroupsForSidebar.map((group) => (
                                            group.items.length > 0 && (
                                                <div key={group.title} className="space-y-1">
                                                    <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{group.title}</p>
                                                    {group.items.map((brand) => (
                                                        <Button
                                                            key={brand.id ?? 'default'}
                                                            variant={selectedBrands.some(b => b.id === brand.id) ? "default" : "ghost"}
                                                            className={`w-full justify-between ${selectedBrands.some(b => b.id === brand.id) ? 'bg-black text-white hover:bg-black/90' : ''}`}
                                                            onClick={() => handleBrandClick(brand)}
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                {getSidebarBrandLogo(brand) && (
                                                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white ring-1 ring-black/10 shadow-sm shrink-0">
                                                                        <img src={getSidebarBrandLogo(brand) as string} alt={brand.name} className="h-4 w-4 object-contain" />
                                                                    </span>
                                                                )}
                                                                {brand.name}
                                                            </span>
                                                            {selectedBrands.some(b => b.id === brand.id) && <Badge variant="secondary">✓</Badge>}
                                                        </Button>
                                                    ))}
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-xl border bg-gray-50 p-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Paso 3 · Categoría</p>
                                        {selectedCategories.length > 0 && (
                                            <Button variant="ghost" size="sm" onClick={() => setSelectedCategories([])}>
                                                <X className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                    <div className="mt-2 space-y-1">
                                        {filteredCategoriesForSidebar.map((category) => (
                                            <Button
                                                key={category.id ?? 'default'}
                                                variant={selectedCategories.some(c => c.id === category.id) ? "default" : "ghost"}
                                                className="w-full justify-between"
                                                onClick={() => handleCategoryClick(category)}
                                            >
                                                {category.name}
                                                {selectedCategories.some(c => c.id === category.id) && <Badge variant="secondary">✓</Badge>}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-xl border bg-gray-50 p-3 space-y-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Paso 4 · Presupuesto y orden</p>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            id="mobile-sidebar-price-mode"
                                            checked={showInitialPrice}
                                            onCheckedChange={setShowInitialPrice}
                                            className="data-[state=checked]:bg-[#E10F18]"
                                        />
                                        <Label htmlFor="mobile-sidebar-price-mode" className="text-xs font-semibold text-gray-700">
                                            {showInitialPrice ? "Mostrar precio total" : "Mostrar precio inicial"}
                                        </Label>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700 mb-1 block">Inicial disponible</Label>
                                        <NumericFormat
                                            value={myInit}
                                            onValueChange={handleInitialChange}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            prefix="RD$ "
                                            className="flex bg-white h-9 w-full rounded-md border border-input px-3 py-2 text-sm"
                                            placeholder="RD$ 0"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700 mb-1 block">Ordenar por</Label>
                                        <Select value={sortMode} onValueChange={(v) => setSortMode(v as typeof sortMode)}>
                                            <SelectTrigger className="h-9 bg-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="featured">Destacados</SelectItem>
                                                <SelectItem value="price-asc">Precio menor a mayor</SelectItem>
                                                <SelectItem value="price-desc">Precio mayor a menor</SelectItem>
                                                <SelectItem value="newest">Año más reciente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Desktop Sidebar fixed (mismo layout que repuestos: colapsable, header lo respeta) */}
            <aside
                className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-40 bg-[#1F2240] border-r border-white/10 shadow-2xl transition-[width,transform] duration-300 ease-in-out ${
                    sidebarCollapsed ? 'w-0 -translate-x-full overflow-hidden' : 'w-80 translate-x-0'
                }`}
            >
                <div className="flex min-w-80 flex-col h-full w-80">
                    <div className="px-5 pt-6 pb-4 border-b border-white/10">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-200/90">Panel de catálogo</p>
                        <h2 className="text-xl font-semibold text-white mt-1">Filtrado inteligente</h2>
                        <p className="text-xs text-white/70 mt-1">Busca, filtra y ordena como un dashboard comercial.</p>
                    </div>

                    <ScrollArea className="flex-1">
                        <div className="p-4 space-y-4">
                            <div className="rounded-xl bg-white/10 border border-white/20 p-3 space-y-2">
                                <Label className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Buscar</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                                    <Input
                                        type="text"
                                        placeholder="Nombre, marca o modelo..."
                                        value={searchText}
                                        onChange={handleSearch}
                                        className="pl-9 h-10 bg-white/10 border-white/20 text-white placeholder:text-white/55"
                                    />
                                </div>
                                {activeFiltersCount > 0 && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={clearFilters}
                                        className="w-full bg-white text-[#1F2240] hover:bg-white/90"
                                    >
                                        Limpiar filtros ({activeFiltersCount})
                                    </Button>
                                )}
                            </div>

                            <div className="rounded-xl bg-white/10 border border-white/20 p-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Paso 1 · Tipo</p>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    <Button size="sm" variant={selectedVehicleType === 'all' ? 'default' : 'outline'} onClick={() => setSelectedVehicleType('all')}>Todos</Button>
                                    <Button size="sm" variant={selectedVehicleType === VehicleCategoryType.MOTORCYCLE ? 'default' : 'outline'} onClick={() => setSelectedVehicleType(VehicleCategoryType.MOTORCYCLE)}>Motos</Button>
                                    <Button size="sm" variant={selectedVehicleType === VehicleCategoryType.AUTOMOBILE ? 'default' : 'outline'} onClick={() => setSelectedVehicleType(VehicleCategoryType.AUTOMOBILE)}>Carros</Button>
                                </div>
                                <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Condición</p>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    <Button size="sm" variant={selectedCondition === 'all' ? 'default' : 'outline'} onClick={() => setSelectedCondition('all')}>Todas</Button>
                                    <Button size="sm" variant={selectedCondition === VehicleCondition.NEW ? 'default' : 'outline'} onClick={() => setSelectedCondition(VehicleCondition.NEW)}>Nuevos</Button>
                                    <Button size="sm" variant={selectedCondition === VehicleCondition.USED ? 'default' : 'outline'} onClick={() => setSelectedCondition(VehicleCondition.USED)}>Usados</Button>
                                </div>
                            </div>

                            <div className="rounded-xl bg-white/10 border border-white/20 p-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Paso 2 · Marca</p>
                                    {selectedBrands.length > 0 && (
                                        <Button variant="ghost" size="sm" onClick={() => setSelectedBrands([])}>
                                            <X className="h-4 w-4 text-white" />
                                        </Button>
                                    )}
                                </div>
                                <div className="mt-2 space-y-3">
                                    {brandGroupsForSidebar.map((group) => (
                                        group.items.length > 0 && (
                                            <div key={group.title} className="space-y-1">
                                                <p className="text-[10px] uppercase tracking-wide text-white/70">{group.title}</p>
                                                {group.items.map((brand) => (
                                                    <Button
                                                        key={brand.id ?? 'default'}
                                                        variant={selectedBrands.some(b => b.id === brand.id) ? "default" : "ghost"}
                                                        className={`w-full justify-between text-white ${selectedBrands.some(b => b.id === brand.id) ? 'bg-black text-white hover:bg-black/90' : 'hover:bg-white/10'}`}
                                                        onClick={() => handleBrandClick(brand)}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            {getSidebarBrandLogo(brand) && (
                                                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white ring-1 ring-black/10 shadow-sm shrink-0">
                                                                    <img src={getSidebarBrandLogo(brand) as string} alt={brand.name} className="h-4 w-4 object-contain" />
                                                                </span>
                                                            )}
                                                            {brand.name}
                                                        </span>
                                                        {selectedBrands.some(b => b.id === brand.id) && <Badge variant="secondary">✓</Badge>}
                                                    </Button>
                                                ))}
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-xl bg-white/10 border border-white/20 p-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Paso 3 · Categoría</p>
                                    {selectedCategories.length > 0 && (
                                        <Button variant="ghost" size="sm" onClick={() => setSelectedCategories([])}>
                                            <X className="h-4 w-4 text-white" />
                                        </Button>
                                    )}
                                </div>
                                <div className="mt-2 space-y-1">
                                    {filteredCategoriesForSidebar.map((category) => (
                                        <Button
                                            key={category.id ?? 'default'}
                                            variant={selectedCategories.some(c => c.id === category.id) ? "default" : "ghost"}
                                            className="w-full justify-between text-white hover:bg-white/10"
                                            onClick={() => handleCategoryClick(category)}
                                        >
                                            {category.name}
                                            {selectedCategories.some(c => c.id === category.id) && <Badge variant="secondary">✓</Badge>}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-xl bg-white/10 border border-white/20 p-3 space-y-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Paso 4 · Presupuesto y orden</p>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        id="desktop-sidebar-price-mode"
                                        checked={showInitialPrice}
                                        onCheckedChange={setShowInitialPrice}
                                        className="data-[state=checked]:bg-[#E10F18]"
                                    />
                                    <Label htmlFor="desktop-sidebar-price-mode" className="text-xs font-semibold text-white">
                                        {showInitialPrice ? "Mostrar precio total" : "Mostrar precio inicial"}
                                    </Label>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-white mb-1 block">Inicial disponible</Label>
                                    <NumericFormat
                                        value={myInit}
                                        onValueChange={handleInitialChange}
                                        thousandSeparator=","
                                        decimalSeparator="."
                                        prefix="RD$ "
                                        className="flex bg-white/20 text-white h-10 w-full rounded-md border border-white/30 px-3 py-2 text-sm placeholder:text-white/50"
                                        placeholder="RD$ 0"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-white mb-1 block">Ordenar por</Label>
                                    <Select value={sortMode} onValueChange={(v) => setSortMode(v as typeof sortMode)}>
                                        <SelectTrigger className="h-9 bg-white/20 text-white border-white/30">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="featured">Destacados</SelectItem>
                                            <SelectItem value="price-asc">Precio menor a mayor</SelectItem>
                                            <SelectItem value="price-desc">Precio mayor a menor</SelectItem>
                                            <SelectItem value="newest">Año más reciente</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </ScrollArea>

                    <div className="p-4 border-t border-white/10">
                        <p className="text-xs text-center text-white/60">
                            © Oriental Ramirez {new Date().getFullYear()}
                            <br />
                            Todos los derechos reservados
                        </p>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div
                className={`flex flex-col flex-1 transition-[padding] duration-300 ${
                    sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-80'
                }`}
            >
                {/* Results Area */}
                <div className="flex flex-1 flex-col p-4 lg:p-6">
                    {/* Buscador del catálogo — se abre/cierra con el icono del header */}
                    {searchOpen && (
                        <div className="relative mb-3">
                            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                            <Input
                                type="text"
                                value={searchText}
                                onChange={handleSearch}
                                placeholder="Buscar por modelo, marca o categoría..."
                                aria-label="Buscar vehículos"
                                autoFocus
                                className="h-12 rounded-xl border-gray-200 bg-white pl-11 pr-10 text-base shadow-sm"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchText('')
                                    setSearchOpen(false)
                                }}
                                aria-label="Cerrar búsqueda"
                                className="absolute right-2.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    {/* Filtro de tipo — prompt inteligente si la búsqueda trae tipos mixtos */}
                    {showTypePrompt ? (
                        <div className="mb-4 rounded-2xl border border-[#E10F18]/25 bg-linear-to-br from-[#FFF4F4] to-white p-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E10F18]/10 text-[#E10F18]">
                                    <Sparkles className="h-5 w-5" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-[#1F2240] sm:text-base">
                                        Tu búsqueda incluye carros y motocicletas
                                    </p>
                                    <p className="text-xs text-gray-500 sm:text-sm">¿Qué te interesa ver?</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setTypePromptDismissed(true)}
                                    aria-label="Descartar"
                                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                <button
                                    type="button"
                                    onClick={() => chooseVehicleType(VehicleCategoryType.MOTORCYCLE)}
                                    className="inline-flex flex-col items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-2.5 text-xs font-semibold text-[#1F2240] transition-colors hover:border-[#E10F18] hover:bg-[#E10F18]/5"
                                >
                                    <Bike className="h-4 w-4" />
                                    Motocicletas
                                </button>
                                <button
                                    type="button"
                                    onClick={() => chooseVehicleType(VehicleCategoryType.AUTOMOBILE)}
                                    className="inline-flex flex-col items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-2.5 text-xs font-semibold text-[#1F2240] transition-colors hover:border-[#E10F18] hover:bg-[#E10F18]/5"
                                >
                                    <Car className="h-4 w-4" />
                                    Carros
                                </button>
                                <button
                                    type="button"
                                    onClick={() => chooseVehicleType('all')}
                                    className="inline-flex flex-col items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-2.5 text-xs font-semibold text-[#1F2240] transition-colors hover:border-[#E10F18] hover:bg-[#E10F18]/5"
                                >
                                    <Layers className="h-4 w-4" />
                                    Ver todo
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-4 grid grid-cols-3 gap-1 rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm">
                            {([
                                { value: 'all' as const, label: 'Todos', icon: Layers },
                                { value: VehicleCategoryType.MOTORCYCLE, label: 'Motos', icon: Bike },
                                { value: VehicleCategoryType.AUTOMOBILE, label: 'Carros', icon: Car },
                            ]).map(({ value, label, icon: Icon }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => chooseVehicleType(value)}
                                    aria-pressed={selectedVehicleType === value}
                                    className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-semibold transition-colors ${
                                        selectedVehicleType === value
                                            ? 'bg-[#1F2240] text-white shadow-sm'
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}

                    {loading ? (
                        <div className="space-y-6">
                            <div className="h-9 w-48 animate-pulse rounded-lg bg-gray-200/80" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm"
                                    >
                                        <div className="aspect-[4/3] animate-pulse bg-gray-200/80" />
                                        <div className="space-y-3 p-4">
                                            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200/80" />
                                            <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200/70" />
                                            <div className="h-9 w-full animate-pulse rounded-lg bg-gray-200/60" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : catalogVehicles.length === 0 ? (
                        <EmptyResult />
                    ) : (
                        <div className="space-y-6">
                            {/* Barra de resultados */}
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200/80 bg-white px-4 py-3 shadow-sm">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-lg font-bold text-[#1F2240]">
                                        {catalogVehicles.length}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        {catalogVehicles.length === 1 ? 'vehículo' : 'vehículos'}
                                        {activeFiltersCount > 0 ? ' encontrados' : ' disponibles'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
                                    {([
                                        { mode: 'grid' as const, icon: LayoutGrid, label: 'Vista en tarjetas grandes' },
                                        { mode: 'compact' as const, icon: Grid3x3, label: 'Vista en cuadrícula compacta' },
                                        { mode: 'list' as const, icon: List, label: 'Vista en lista' },
                                    ]).map(({ mode, icon: Icon, label }) => (
                                        <Button
                                            key={mode}
                                            variant={viewMode === mode ? 'default' : 'ghost'}
                                            size="sm"
                                            onClick={() => setViewMode(mode)}
                                            aria-label={label}
                                            aria-pressed={viewMode === mode}
                                            className={viewMode === mode ? 'bg-[#1F2240] hover:bg-[#2A285D]' : ''}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {viewMode === 'list' ? (
                                <div className="flex flex-col gap-4">
                                    {currentItems.map((vehicle) => (
                                        <VehicleListItem
                                            key={vehicle.id}
                                            vehicle={vehicle}
                                            onClick={() => handleVehicleClick(vehicle)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div
                                    className={
                                        viewMode === 'compact'
                                            ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3'
                                            : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5'
                                    }
                                >
                                    {currentItems.map((vehicle) => (
                                        <div
                                            key={vehicle.id}
                                            onClick={() => handleVehicleClick(vehicle)}
                                            className="cursor-pointer"
                                        >
                                            <VehicleCard
                                                {...vehicle}
                                                dense={viewMode === 'compact'}
                                                displayPrice={getDisplayPrice(vehicle)}
                                                secondaryPrice={getSecondaryPrice(vehicle)}
                                                showInitialPrice={showInitialPrice}
                                                displayCurrency={currency}
                                                onClick={() => handleVehicleClick(vehicle)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pagination - Desktop */}
                            {catalogVehicles.length > 0 && (
                                <div className="hidden lg:flex bg-white rounded-xl border border-gray-200/80 p-4 mt-6">
                                    <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-4">
                                        <Select onValueChange={handleChangeItemsPerPage} value={itemsPerPage.toString()}>
                                            <SelectTrigger className="w-[180px]">
                                                <SelectValue placeholder="Vehículos por página" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="4">4 por página</SelectItem>
                                                <SelectItem value="8">8 por página</SelectItem>
                                                <SelectItem value="12">12 por página</SelectItem>
                                                <SelectItem value="16">16 por página</SelectItem>
                                            </SelectContent>
                                        </Select>

                                        <Pagination>
                                            <PaginationContent>
                                                <PaginationItem>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleChangePage(Math.max(1, currentPage - 1))}
                                                        disabled={currentPage === 1}
                                                        className="text-sm"
                                                    >
                                                        Anterior
                                                    </Button>
                                                </PaginationItem>
                                                {Array.from({ length: pageCount }, (_, i) => i + 1)
                                                    .filter(page => {
                                                        const distance = Math.abs(page - currentPage)
                                                        return distance === 0 || distance === 1 || page === 1 || page === pageCount
                                                    })
                                                    .map((page, index, array) => {
                                                        if (index > 0 && array[index - 1] !== page - 1) {
                                                            return [
                                                                <PaginationItem key={`ellipsis-${page}`}>
                                                                    <PaginationLink>...</PaginationLink>
                                                                </PaginationItem>,
                                                                <PaginationItem key={page}>
                                                                    <PaginationLink
                                                                        onClick={() => handleChangePage(page)}
                                                                        isActive={currentPage === page}
                                                                    >
                                                                        {page}
                                                                    </PaginationLink>
                                                                </PaginationItem>
                                                            ]
                                                        }
                                                        return (
                                                            <PaginationItem key={page}>
                                                                <PaginationLink
                                                                    onClick={() => handleChangePage(page)}
                                                                    isActive={currentPage === page}
                                                                >
                                                                    {page}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        )
                                                    })}
                                                <PaginationItem>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleChangePage(Math.min(pageCount, currentPage + 1))}
                                                        disabled={currentPage === pageCount}
                                                        className="text-sm"
                                                    >
                                                        Siguiente
                                                    </Button>
                                                </PaginationItem>
                                            </PaginationContent>
                                        </Pagination>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Mobile Pagination - in flow */}
                {catalogVehicles.length > 0 && (
                    <div className="lg:hidden bg-white border-t border-gray-200 p-3">
                        <div className="flex justify-center">
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleChangePage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="text-sm"
                                        >
                                            Anterior
                                        </Button>
                                    </PaginationItem>
                                    
                                    <PaginationItem>
                                        <span className="px-4 text-sm text-gray-600">
                                            {currentPage} de {pageCount}
                                        </span>
                                    </PaginationItem>

                                    <PaginationItem>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleChangePage(Math.min(pageCount, currentPage + 1))}
                                            disabled={currentPage === pageCount}
                                            className="text-sm"
                                        >
                                            Siguiente
                                        </Button>
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    </div>
                )}

                <AppCatalogFooter activeLink="catalogo" />
            </div>
        </div>
    )
}